// Render inline emphasis without showing raw asterisks: **bold**/__bold__ -> <strong>,
// *italic*/_italic_ -> <em>. Newlines are preserved by the container's whitespace-pre-wrap.
// Unpaired markers are left as-is. Returns an array of React nodes safe to drop into JSX.
export function renderEmphasis(text) {
  const str = text == null ? "" : String(text);
  if (!str) return [];
  const nodes = [];
  const re = /\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|(?<![A-Za-z0-9])\*([^*\n]+?)\*(?![A-Za-z0-9])|(?<![A-Za-z0-9])_([^_\n]+?)_(?![A-Za-z0-9])/g;
  let last = 0;
  let key = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) nodes.push(str.slice(last, m.index));
    if (m[1] != null || m[2] != null) {
      nodes.push(<strong key={`b${key++}`}>{m[1] != null ? m[1] : m[2]}</strong>);
    } else {
      nodes.push(<em key={`i${key++}`}>{m[3] != null ? m[3] : m[4]}</em>);
    }
    last = re.lastIndex;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
}

// Coaching emphasis: like renderEmphasis, but ALSO bolds the material that matters most in a coaching
// turn — the question being asked, and quoted phrases (the student's words / themes / options under
// discussion). Keeps markdown **bold**/*italic* too.
export function renderCoaching(text) {
  const str = text == null ? "" : String(text);
  if (!str) return [];
  const nodes = [];
  let key = 0;
  let last = 0;
  let m;
  // Priority: markdown bold, markdown italic, questions (clause ending in ?), quoted spans.
  const re = /\*\*([^*\n]+?)\*\*|(?<![A-Za-z0-9])\*([^*\n]+?)\*(?![A-Za-z0-9])|([^\n.!?;:]*\?)|"([^"\n]+?)"|[\u201C]([^\u201D\n]+?)[\u201D]|[\u2018]([^\u2019\n]+?)[\u2019]/g;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) nodes.push(str.slice(last, m.index));
    if (m[1] != null) {
      nodes.push(<strong key={`b${key++}`}>{m[1]}</strong>);
    } else if (m[2] != null) {
      nodes.push(<em key={`i${key++}`}>{m[2]}</em>);
    } else {
      // question (m[3]) or quoted span (m[0]) — bold the whole matched text, keeping punctuation/quotes
      const seg = m[0];
      if (!seg.trim()) { nodes.push(seg); }
      else nodes.push(<strong key={`q${key++}`}>{seg}</strong>);
    }
    last = re.lastIndex;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
}

// TEACH → CONNECT → ACT presentation. Detects the light instructional headings The Writing Studio emits on a
// teaching turn (WRITING PRINCIPLE — X / SENTENCE PRINCIPLE — X / PROOFREADING RULE — X / IN YOUR
// PARAGRAPH|WRITING|SENTENCE|DRAFT / TRY THIS) and renders each as a small scannable label above its
// text. If no headings are present (ordinary dialogue follow-ups), falls back to plain conversation
// rendering so the thread still feels like a conversation.
const _HEAD_RE = /^\s*(WRITING PRINCIPLE|SENTENCE PRINCIPLE|PARAGRAPH PRINCIPLE|PROOFREADING RULE|WRITING RULE|IN YOUR PARAGRAPH|IN YOUR WRITING|IN YOUR SENTENCE|IN YOUR DRAFT|LET[’']S WORK (?:IT|THIS) OUT|TRY THIS|NOW TRY)\b\s*[—:\-]?\s*(.*)$/i;
const _HEAD_START = /^(WRITING PRINCIPLE|SENTENCE PRINCIPLE|PARAGRAPH PRINCIPLE|PROOFREADING RULE|WRITING RULE|IN YOUR|LET[’']S WORK|TRY THIS|NOW TRY)/i;

export function renderCoachingRich(text) {
  const str = text == null ? "" : String(text);
  if (!str.trim()) return [];
  const lines = str.split(/\n/);
  const hasHeading = lines.some((l) => _HEAD_START.test(l.trim()) && _HEAD_RE.test(l.trim()));
  if (!hasHeading) return renderCoaching(str);

  const blocks = [];
  let cur = null;
  const flush = () => { if (cur && (cur.label || cur.body.join("").trim())) blocks.push(cur); cur = null; };
  for (const raw of lines) {
    const line = raw.trim();
    const isHead = _HEAD_START.test(line) && _HEAD_RE.test(line);
    if (isHead) {
      const m = line.match(_HEAD_RE);
      const kw = m[1].toUpperCase();
      const rest = (m[2] || "").trim();
      flush();
      cur = { label: kw, kw, body: [] };
      if (/PRINCIPLE|RULE/.test(kw)) {
        if (rest && rest.split(/\s+/).length <= 5 && !/[.?!]/.test(rest)) {
          cur.label = kw + " — " + rest.replace(/[:—\-]+$/, "").trim().toUpperCase();
        } else if (rest) {
          cur.body.push(rest);
        }
      } else if (rest) {
        cur.body.push(rest);
      }
    } else {
      if (!cur) cur = { label: null, kw: null, body: [] };
      cur.body.push(raw);
    }
  }
  flush();

  const labelCls = (kw) => {
    if (!kw) return "";
    if (/PRINCIPLE|RULE/.test(kw)) return "text-[#8C3A2A]";
    if (/TRY THIS|NOW TRY|LET.S WORK/.test(kw)) return "text-stone-700";
    return "text-stone-500";
  };

  return blocks.map((b, i) => {
    const bodyText = b.body.join("\n").replace(/^\n+|\n+$/g, "");
    return (
      <div key={`blk${i}`} className={i > 0 ? "mt-3" : ""} data-testid={`coaching-chunk-${i}`}>
        {b.label && (
          <p className={`mb-1 font-mono-panel text-[10px] font-semibold uppercase tracking-[0.16em] ${labelCls(b.kw)}`}>
            {b.label}
          </p>
        )}
        {bodyText && <p className="whitespace-pre-wrap">{renderCoaching(bodyText)}</p>}
      </div>
    );
  });
}
